import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    fetchGlImportHistory,
    fetchGlImportHistoryDetail,
    type GlColumnDef,
    type GlImportAmountDetailSnap,
    type GlImportBatchDetailVO,
    type GlImportBatchVO,
    type GlImportInvoiceChange,
    type GlImportInvoiceSnap,
} from '@/services/accountService/glInvoiceService';

export type GlImportHistoryDialogProps = {
    open: boolean;
    companyId: number;
    annualId: number;
    companyName?: string;
    /** 用于把 FS/GL code 解析成名称（含公司别名） */
    columnDefs?: GlColumnDef[];
    onClose: () => void;
};

function formatTime(value?: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
}

function formatMoney(value?: number | null): string {
    if (value == null || Number.isNaN(Number(value))) return '';
    return Number(value).toLocaleString(undefined, {maximumFractionDigits: 2});
}

function changeTypeLabel(type?: string | null): string {
    switch ((type || '').toUpperCase()) {
        case 'UPDATE':
            return 'Updated';
        case 'DELETE':
            return 'Deleted';
        case 'INSERT':
            return 'Inserted';
        default:
            return type || 'Changed';
    }
}

function changeTypeColor(type?: string | null): 'default' | 'warning' | 'error' | 'success' {
    switch ((type || '').toUpperCase()) {
        case 'UPDATE':
            return 'warning';
        case 'DELETE':
            return 'error';
        case 'INSERT':
            return 'success';
        default:
            return 'default';
    }
}

function displaySnap(snap?: GlImportInvoiceSnap | null): GlImportInvoiceSnap | null {
    return snap ?? null;
}

type View = 'list' | 'detail';

/** GL Excel 覆盖导入审计痕迹（只读，无恢复）。 */
export default function GlImportHistoryDialog({
    open,
    companyId,
    annualId,
    companyName,
    columnDefs = [],
    onClose,
}: GlImportHistoryDialogProps) {
    const [view, setView] = useState<View>('list');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [batches, setBatches] = useState<GlImportBatchVO[]>([]);
    const [detail, setDetail] = useState<GlImportBatchDetailVO | null>(null);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [invPage, setInvPage] = useState(0);
    const [invPageSize, setInvPageSize] = useState(25);

    const loadBatches = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await fetchGlImportHistory(companyId, annualId);
            setBatches(list || []);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load import history');
            setBatches([]);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId]);

    useEffect(() => {
        if (!open) return;
        setView('list');
        setDetail(null);
        setSelectedKey(null);
        setInvPage(0);
        void loadBatches();
    }, [open, loadBatches]);

    const openBatch = useCallback(async (batchId: number) => {
        setDetailLoading(true);
        setError(null);
        setSelectedKey(null);
        setInvPage(0);
        try {
            const data = await fetchGlImportHistoryDetail(batchId);
            setDetail(data);
            setView('detail');
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load batch detail');
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const changes: GlImportInvoiceChange[] = useMemo(() => {
        if (detail?.changes && detail.changes.length > 0) {
            return detail.changes;
        }
        // 兼容旧 API：仅 before 扁平列表
        return (detail?.invoices ?? []).map((inv) => ({
            changeType: 'UPDATE',
            invoiceId: inv.invoiceId,
            before: inv,
            after: null,
            beforeDetails: (detail?.amountDetails ?? []).filter((d) => d.invoiceId === inv.invoiceId),
            afterDetails: [],
        }));
    }, [detail]);

    const pagedChanges = useMemo(
        () => changes.slice(invPage * invPageSize, invPage * invPageSize + invPageSize),
        [changes, invPage, invPageSize],
    );

    const selectedChange = useMemo(
        () => changes.find((c) => `${c.invoiceId}|${c.changeType}` === selectedKey) ?? null,
        [changes, selectedKey],
    );

    const selectChange = useCallback((change: GlImportInvoiceChange) => {
        setSelectedKey(`${change.invoiceId}|${change.changeType}`);
        setError(null);
    }, []);

    const fsNameByCode = useMemo(() => {
        const map = new Map<string, string>();
        for (const def of columnDefs) {
            const code = (def.fsCode || '').trim();
            if (!code || map.has(code)) continue;
            const name = (def.fsNameEn || '').trim();
            if (name) map.set(code, name);
        }
        return map;
    }, [columnDefs]);

    const glNameByKey = useMemo(() => {
        const byPair = new Map<string, string>();
        const byGl = new Map<string, string>();
        for (const def of columnDefs) {
            const fs = (def.fsCode || '').trim();
            const gl = (def.glCode || '').trim();
            const name = (def.nameEn || '').trim();
            if (!name) continue;
            if (fs || gl) byPair.set(`${fs}|${gl}`, name);
            if (gl && !byGl.has(gl)) byGl.set(gl, name);
        }
        return {byPair, byGl};
    }, [columnDefs]);

    const resolveFsLabel = useCallback((fsCode?: string | null) => {
        const code = (fsCode || '').trim();
        if (!code) return '';
        return fsNameByCode.get(code) || code;
    }, [fsNameByCode]);

    const resolveGlLabel = useCallback((fsCode?: string | null, glCode?: string | null) => {
        const fs = (fsCode || '').trim();
        const gl = (glCode || '').trim();
        if (!fs && !gl) return '';
        return glNameByKey.byPair.get(`${fs}|${gl}`)
            || glNameByKey.byGl.get(gl)
            || gl
            || fs;
    }, [glNameByKey]);

    const handleClose = () => {
        onClose();
    };

    const renderInvoiceFields = (snap: GlImportInvoiceSnap | null, emptyHint: string) => {
        if (!snap) {
            return (
                <Typography color="text.secondary" variant="body2" sx={{py: 1}}>
                    {emptyHint}
                </Typography>
            );
        }
        return (
            <Table size="small">
                <TableBody>
                    <TableRow>
                        <TableCell width={110} sx={{color: 'text.secondary'}}>Date</TableCell>
                        <TableCell>{snap.date || '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{color: 'text.secondary'}}>Description</TableCell>
                        <TableCell>{snap.description || ''}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{color: 'text.secondary'}}>Voucher</TableCell>
                        <TableCell>{snap.voucherNo || ''}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{color: 'text.secondary'}}>Invoice No.</TableCell>
                        <TableCell>{snap.invoiceNo || ''}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{color: 'text.secondary'}}>Details</TableCell>
                        <TableCell>{snap.details || ''}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{color: 'text.secondary'}}>Price</TableCell>
                        <TableCell>{formatMoney(snap.price)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        );
    };

    const renderAmountTable = (rows: GlImportAmountDetailSnap[], emptyHint: string) => (
        <TableContainer sx={{maxHeight: 180}}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>FS</TableCell>
                        <TableCell>GL</TableCell>
                        <TableCell>Currency</TableCell>
                        <TableCell align="right">Amount</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <Typography color="text.secondary" variant="body2">
                                    {emptyHint}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : rows.map((d) => (
                        <TableRow key={d.historyId}>
                            <TableCell title={d.fsCode || ''}>
                                {resolveFsLabel(d.fsCode)}
                            </TableCell>
                            <TableCell title={d.glCode || ''}>
                                {resolveGlLabel(d.fsCode, d.glCode)}
                            </TableCell>
                            <TableCell>{d.currency || ''}</TableCell>
                            <TableCell align="right">{formatMoney(d.amount)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{pb: 1}}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {view === 'detail' ? (
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon/>}
                            onClick={() => {
                                setView('list');
                                setDetail(null);
                                setSelectedKey(null);
                            }}
                            sx={{textTransform: 'none'}}
                        >
                            Batches
                        </Button>
                    ) : null}
                    <Typography variant="h6" fontWeight={700} component="span">
                        Import History
                        {companyName ? ` — ${companyName}` : ''}
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                    Read-only audit trail. Only changed / soft-deleted rows are kept.
                    Updated rows show Before vs After; deleted rows show Before only.
                    Written = inserted; Updated = content-changed.
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{minHeight: 420}}>
                {error ? (
                    <Typography color="error" variant="body2" sx={{mb: 1}}>{error}</Typography>
                ) : null}

                {(loading || detailLoading) ? (
                    <Box sx={{textAlign: 'center', py: 6}}>
                        <CircularProgress size={28}/>
                    </Box>
                ) : view === 'list' ? (
                    batches.length === 0 ? (
                        <Typography color="text.secondary" sx={{py: 4, textAlign: 'center'}}>
                            No import history for this company / year yet.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Time</TableCell>
                                        <TableCell>Mode</TableCell>
                                        <TableCell align="right">Snapshot</TableCell>
                                        <TableCell align="right">Written</TableCell>
                                        <TableCell align="right">Updated</TableCell>
                                        <TableCell align="right">Errors</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>IP</TableCell>
                                        <TableCell/>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {batches.map((b) => {
                                        const isReplace = b.replaceExisting === 1
                                            || b.operationSource === 'EXCEL_UPLOAD'
                                            || b.operationSource === 'WEB_REPLACE';
                                        return (
                                        <TableRow key={b.batchId} hover>
                                            <TableCell>{formatTime(b.changeTime)}</TableCell>
                                            <TableCell>
                                                {isReplace ? 'Replace' : (b.operationSource || '—')}
                                            </TableCell>
                                            <TableCell align="right">
                                                {b.prevInvoiceCount ?? 0}
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    {` / ${b.prevDetailCount ?? 0} det`}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {b.newInsertedCount ?? '—'}
                                            </TableCell>
                                            <TableCell align="right">
                                                {b.newUpdatedCount ?? '—'}
                                            </TableCell>
                                            <TableCell align="right">{b.newErrorCount ?? '—'}</TableCell>
                                            <TableCell>{b.changeUserId ?? '—'}</TableCell>
                                            <TableCell>{b.operationIp || '—'}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    sx={{textTransform: 'none'}}
                                                    onClick={() => void openBatch(b.batchId)}
                                                >
                                                    View changes
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                ) : (
                    <Stack spacing={2}>
                        {detail?.batch ? (
                            <Typography variant="body2" color="text.secondary">
                                Batch #{detail.batch.batchId}
                                {' · '}
                                {formatTime(detail.batch.changeTime)}
                                {' · '}
                                Snapshot {detail.batch.prevInvoiceCount ?? 0} invoices
                                {' / '}
                                {detail.batch.prevDetailCount ?? 0} details
                                {' → written '}
                                {detail.batch.newInsertedCount ?? '—'}
                                {' / updated '}
                                {detail.batch.newUpdatedCount ?? '—'}
                            </Typography>
                        ) : null}

                        <Typography variant="subtitle2" fontWeight={600}>
                            Changed / deleted rows ({changes.length})
                        </Typography>
                        <TableContainer sx={{maxHeight: 240}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={90}>Type</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Invoice No.</TableCell>
                                        <TableCell align="right">Before price</TableCell>
                                        <TableCell align="right">After price</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pagedChanges.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Typography color="text.secondary" variant="body2">
                                                    No changed rows in this batch (all matched rows identical, or first import).
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : pagedChanges.map((change) => {
                                        const key = `${change.invoiceId}|${change.changeType}`;
                                        const before = displaySnap(change.before);
                                        const after = displaySnap(change.after);
                                        const summary = after || before;
                                        return (
                                            <TableRow
                                                key={key}
                                                hover
                                                selected={selectedKey === key}
                                                onClick={() => selectChange(change)}
                                                sx={{cursor: 'pointer'}}
                                            >
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={changeTypeLabel(change.changeType)}
                                                        color={changeTypeColor(change.changeType)}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{summary?.date || '—'}</TableCell>
                                                <TableCell>{summary?.description || ''}</TableCell>
                                                <TableCell>{summary?.invoiceNo || ''}</TableCell>
                                                <TableCell align="right">{formatMoney(before?.price)}</TableCell>
                                                <TableCell align="right">
                                                    {change.changeType === 'DELETE'
                                                        ? '—'
                                                        : formatMoney(after?.price)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {changes.length > 0 ? (
                            <TablePagination
                                component="div"
                                count={changes.length}
                                page={invPage}
                                onPageChange={(_, p) => setInvPage(p)}
                                rowsPerPage={invPageSize}
                                onRowsPerPageChange={(e) => {
                                    setInvPageSize(parseInt(e.target.value, 10));
                                    setInvPage(0);
                                }}
                                rowsPerPageOptions={[25, 50, 100]}
                            />
                        ) : null}

                        {!selectedChange ? (
                            <Typography color="text.secondary" variant="body2">
                                Click a row to compare Before / After.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Compare
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={changeTypeLabel(selectedChange.changeType)}
                                        color={changeTypeColor(selectedChange.changeType)}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {selectedChange.invoiceId}
                                    </Typography>
                                </Stack>
                                <Stack direction={{xs: 'column', md: 'row'}} spacing={2} alignItems="stretch">
                                    <Box sx={{flex: 1, minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1}}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                            Before
                                        </Typography>
                                        {renderInvoiceFields(
                                            displaySnap(selectedChange.before),
                                            selectedChange.changeType === 'INSERT' ? 'New row (no before)' : 'No before snapshot',
                                        )}
                                        <Typography variant="caption" fontWeight={600} sx={{mt: 1, display: 'block'}}>
                                            Amount details
                                        </Typography>
                                        {renderAmountTable(
                                            selectedChange.beforeDetails ?? [],
                                            'No amount details',
                                        )}
                                    </Box>
                                    <Box sx={{flex: 1, minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1}}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                            After
                                        </Typography>
                                        {selectedChange.changeType === 'DELETE' ? (
                                            <Typography color="error.main" variant="body2" sx={{py: 1}}>
                                                Deleted
                                            </Typography>
                                        ) : renderInvoiceFields(
                                            displaySnap(selectedChange.after),
                                            'After not recorded (legacy batch — re-import to capture After)',
                                        )}
                                        {selectedChange.changeType !== 'DELETE' ? (
                                            <>
                                                <Typography variant="caption" fontWeight={600} sx={{mt: 1, display: 'block'}}>
                                                    Amount details
                                                </Typography>
                                                {renderAmountTable(
                                                    selectedChange.afterDetails ?? [],
                                                    selectedChange.after
                                                        ? 'No amount details'
                                                        : 'After not recorded',
                                                )}
                                            </>
                                        ) : null}
                                    </Box>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} sx={{textTransform: 'none'}}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
